import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import Swal from 'sweetalert2';
import './SlotGame2.css';
import SlotgameOpen from './assets/Slotgame-open.mp3';
import SlotTurn from './assets/slot-turn.mp3';
import CountdownSound from './assets/Countdown-15s.wav';
import TrueSound from './assets/true.wav';
import WrongSound from './assets/wrong.wav';
import { db } from '../Firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// สัญลักษณ์สำหรับแต่ละช่อง
const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const OPERATORS = ['+', '-'];

// ฟังก์ชันสำหรับสร้าง Reel แบบสุ่ม
const generateReelSymbols = (finalSymbol, symbolType) => {
  const reel = [];
  const operators = ['+', '-'];
  
  // สร้างสัญลักษณ์แบบสุ่ม 30 ตัวเพื่อเป็นตัวหมุนผ่าน
  for (let i = 0; i < 30; i++) {
    if (symbolType === 'number') {
      // สุ่มเลข 1-9
      const randIndex = Math.floor(Math.random() * NUMBERS.length);
      reel.push(NUMBERS[randIndex]);
    } else {
      reel.push(operators[Math.floor(Math.random() * operators.length)]);
    }
  }
  // ใส่สัญลักษณ์ที่เป็นผลลัพธ์จริงไว้ตัวสุดท้าย
  reel.push(finalSymbol);
  return reel;
};

// Component วงล้อ (Reel)
const Reel = React.memo(({ symbols, symbolType }) => (
  <div className="reel">
    {symbols.map((symbol, i) => (
      <div key={i} className="reel-symbol">
        <span className={`symbol-${symbolType}`}>{symbol}</span>
      </div>
    ))}
  </div>
));

// Component สำหรับนับถอยหลัง
const CountdownTimer = React.memo(({ countdown }) => (
  <div className="countdown">เวลาที่เหลือ: {countdown} วินาที</div>
));

// Component สำหรับจัดการ countdown
const CountdownManager = React.memo(({ 
  isActive, 
  onTimeUp, 
  onCountdownChange, 
  initialCountdown = 15 
}) => {
  const [countdown, setCountdown] = useState(initialCountdown);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isActive && countdown > 0) {
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          const newCount = prev - 1;
          onCountdownChange(newCount);
          if (newCount <= 0) {
            clearInterval(intervalRef.current);
            onTimeUp();
            return initialCountdown;
          }
          return newCount;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, onTimeUp, onCountdownChange, initialCountdown]);

  useEffect(() => {
    if (!isActive) {
      setCountdown(initialCountdown);
    }
  }, [isActive, initialCountdown]);

  return <CountdownTimer countdown={countdown} />;
});

// Component หลักของเกม
export function SlotGame({ userData, onUserUpdate, onStopInformationSound }) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [showAnswerInput, setShowAnswerInput] = useState(false);
  const [countdown, setCountdown] = useState(15);
  const [userAnswer, setUserAnswer] = useState('');
  const [currentResult, setCurrentResult] = useState(null);
  const [slotStopped, setSlotStopped] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [timeoutTimer, setTimeoutTimer] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null);
  
  // 5 Reels: Number Operator Number Operator Number
  const [finalReels, setFinalReels] = useState([
    ['1', '1', '1'], // Number
    ['+', '+', '+'], // Operator
    ['1', '1', '1'], // Number
    ['+', '+', '+'], // Operator
    ['1', '1', '1'], // Number
  ]);
  
  const [reels, setReels] = useState([
    ['1', '1', '1'],
    ['+', '+', '+'],
    ['1', '1', '1'],
    ['+', '+', '+'],
    ['1', '1', '1'],
  ]);

  // เพิ่ม state
  const [answerDisabled, setAnswerDisabled] = useState(false);

  // เพิ่ม ref สำหรับ audio
  const slotOpenAudioRef = useRef(null);
  const slotTurnAudioRef = useRef(null);
  const countdownAudioRef = useRef(null);
  // เสียงตอบถูก/ผิด
  const trueAudioRef = useRef(new Audio(TrueSound));
  const wrongAudioRef = useRef(new Audio(WrongSound));

  // ฟังก์ชันสำหรับอัปเดตคะแนนและสถิติผู้ใช้
  const updateUserScore = useCallback(async (scoreToAdd, isCorrect, currentRoundSpeed = 0) => {

    
    if (!userData?.username) {

      return;
    }

    try {
      const collectionName = userData.collection || "Student-history";
      const docId = userData.id || userData.username;
      
      const userRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(userRef);

      let currentDbScore = 0;
      let currentTotal = 0;
      let currentCount = 0;
      let currentStep2 = 0;
      let currentSpeed = 0;

      if (docSnap.exists()) {
        const dbData = docSnap.data();
        currentDbScore = dbData.score || 0;
        currentTotal = dbData['step2-total'] || 0;
        currentCount = dbData.count || 0;
        currentStep2 = dbData.step2 || 0;
        currentSpeed = dbData['step2-speed'] || 0;
      }

      const newScore = currentDbScore + scoreToAdd;
      const newTotal = currentTotal + 1;
      const newCount = currentCount + 1;
      
      const newStep2 = isCorrect ? currentStep2 + 1 : currentStep2;

      let newSpeed = currentSpeed;
      if (isCorrect) {
        if (currentSpeed === 0) {
          newSpeed = currentRoundSpeed;
        } else {
          newSpeed = (currentRoundSpeed + currentSpeed) / 2;
        }
      }



      await setDoc(userRef, { 
        score: newScore,
        'step2-total': newTotal,
        count: newCount,
        step2: newStep2,
        'step2-speed': newSpeed
      }, { merge: true });
      
      // console.log('Stats updated successfully');
      
      // if (onUserUpdate) {
      //   onUserUpdate(userData.username);
      // }
    } catch (error) {
      console.error("Error updating user stats:", error);
      Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถอัปเดตข้อมูลได้', 'error');
    }
  }, [userData, onUserUpdate]);

  // ไม่เล่นเสียงทันทีเมื่อ component mount
  useEffect(() => {
    return () => {
      if (slotOpenAudioRef.current) {
        slotOpenAudioRef.current.pause();
        slotOpenAudioRef.current.currentTime = 0;
      }
    };
  }, []);



  // Helper to calculate result from 5-element array
  const calculate3NumResult = (items) => {
    // items: [num1, op1, num2, op2, num3]
    if (!items || items.length !== 5) return 0;
    
    // Parse numbers
    const n1 = parseInt(items[0]);
    const op1 = items[1];
    const n2 = parseInt(items[2]);
    const op2 = items[3];
    const n3 = parseInt(items[4]);

    // Calculate first part (left to right)
    let res1 = 0;
    if (op1 === '+') res1 = n1 + n2;
    else if (op1 === '-') res1 = n1 - n2;
    
    // Calculate final
    let finalRes = 0;
    if (op2 === '+') finalRes = res1 + n3;
    else if (op2 === '-') finalRes = res1 - n3;
    
    return finalRes;
  };

  const checkAnswer = useCallback(async (currentResult, scoreValue) => {
    setShowAnswerInput(false);

    if (!currentResult || currentResult.length !== 5) return;
    
    const correctResult = calculate3NumResult(currentResult);

    const userAnswerNum = parseInt(userAnswer) || 0;
    const isCorrect = userAnswerNum === correctResult;
    
    const scoreEarned = 0; // Timeout = 0 score
    
    const newResultData = {
      isCorrect,
      correctResult,
      userAnswer: userAnswerNum,
      scoreEarned
    };
    
    setResultData(newResultData);
    setShowResultModal(true);
    
    if (isCorrect) {
      if (trueAudioRef.current) {
        trueAudioRef.current.pause();
        trueAudioRef.current.currentTime = 0;
        trueAudioRef.current.play().catch(() => {});
      }
    } else {
      if (wrongAudioRef.current) {
        wrongAudioRef.current.pause();
        wrongAudioRef.current.currentTime = 0;
        wrongAudioRef.current.play().catch(() => {});
      }
    }
    
    await updateUserScore(scoreEarned, isCorrect, 0);
    
    Swal.fire({
      title: isCorrect ? 'ถูกต้อง! 🎉' : 'ผิด! 😔',
      text: isCorrect ? `คุณได้ ${scoreEarned} คะแนน!` : `คำตอบที่ถูกต้องคือ ${correctResult} (ได้ 0 คะแนน)`,
      icon: isCorrect ? 'success' : 'error',
      showCloseButton: false,
      showConfirmButton: true,
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      if (onUserUpdate) {
        onUserUpdate(userData.username);
      }
    });
  }, [userAnswer, updateUserScore]);

  const spin = useCallback(() => {
    if (isSpinning) return;
    if (onStopInformationSound) onStopInformationSound();
    if (slotTurnAudioRef.current) {
      slotTurnAudioRef.current.currentTime = 0;
      slotTurnAudioRef.current.play().catch(() => {});
    }

    if (countdownTimer) clearInterval(countdownTimer);
    if (timeoutTimer) clearTimeout(timeoutTimer);

    setIsSpinning(true);
    setSlotStopped(false);
    setUserAnswer('');
    setCountdown(15);
    setShowAnswerInput(false);
    setShowResultModal(false);
    setResultData(null);

    // Loop to find a non-negative result combination
    let finalResult = [];
    let isValid = false;
    let attempts = 0;

    while (!isValid && attempts < 100) {
        const n1 = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
        const n2 = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
        const n3 = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
        const op1 = OPERATORS[Math.floor(Math.random() * OPERATORS.length)];
        const op2 = OPERATORS[Math.floor(Math.random() * OPERATORS.length)];
        
        const tempResult = [n1, op1, n2, op2, n3];
        
        // Check intermediate result (first 2 numbers)
        let intermediateRes = 0;
        const num1 = parseInt(n1);
        const num2 = parseInt(n2);
        
        if (op1 === '+') intermediateRes = num1 + num2;
        else if (op1 === '-') intermediateRes = num1 - num2;
        
        // If intermediate result is negative, skip this combination
        if (intermediateRes < 0) {
             attempts++;
             continue;
        }

        const res = calculate3NumResult(tempResult);
        
        if (res > 0) {
            finalResult = tempResult;
            isValid = true;
        }
        attempts++;
    }
    
    // Fallback if loop fails (highly unlikely with simple + -)
    if (!isValid) {
        finalResult = ['5', '+', '5', '-', '5'];
    }
    
    setCurrentResult(finalResult);
    
    const newReels = [
      generateReelSymbols(finalResult[0], 'number'),
      generateReelSymbols(finalResult[1], 'operator'),
      generateReelSymbols(finalResult[2], 'number'),
      generateReelSymbols(finalResult[3], 'operator'),
      generateReelSymbols(finalResult[4], 'number'),
    ];
    setReels(newReels);

    setTimeout(() => {
      setIsSpinning(false);
      setSlotStopped(true);
      setFinalReels(newReels);
      setShowAnswerInput(true);
      
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowAnswerInput(false);
            checkAnswer(finalResult).catch(console.error);
            return 15;
          }
          return prev - 1;
        });
      }, 1000);
      
      setCountdownTimer(interval);
      
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setShowAnswerInput(false);
        checkAnswer(finalResult).catch(console.error);
      }, 15000);
      
      setTimeoutTimer(timeout);

      if (countdownAudioRef.current) {
        countdownAudioRef.current.currentTime = 0;
        countdownAudioRef.current.play().catch(() => {});
      }
    }, 4000); 
    setAnswerDisabled(false); 
  }, [isSpinning, countdownTimer, timeoutTimer, checkAnswer, onStopInformationSound]);

  const handleSubmit = useCallback(async () => {
    if (currentResult && currentResult.length === 5) {
      if (countdownTimer) {
        clearInterval(countdownTimer);
        setCountdownTimer(null);
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        setTimeoutTimer(null);
      }
      
      if (countdownAudioRef.current) {
        countdownAudioRef.current.pause();
        countdownAudioRef.current.currentTime = 0;
      }

      const correctResult = calculate3NumResult(currentResult);

      const userAnswerNum = parseInt(userAnswer);
      const isCorrect = userAnswerNum === correctResult;
      
      // Calculate score (Max 20)
      const scoreEarned = isCorrect ? Math.round((countdown / 15) * 20) : 0;
      
      const newResultData = {
        isCorrect,
        correctResult,
        userAnswer: userAnswerNum,
        scoreEarned
      };
      
      setResultData(newResultData);
      setShowResultModal(true);
      setAnswerDisabled(true); 
      
      if (isCorrect) {
        if (trueAudioRef.current) {
          trueAudioRef.current.pause();
          trueAudioRef.current.currentTime = 0;
          trueAudioRef.current.play().catch(() => {});
        }
      } else {
        if (wrongAudioRef.current) {
          wrongAudioRef.current.pause();
          wrongAudioRef.current.currentTime = 0;
          wrongAudioRef.current.play().catch(() => {});
        }
      }
      
      const currentRoundSpeed = isCorrect ? (countdown / 15) * 100 : 0;
      await updateUserScore(scoreEarned, isCorrect, currentRoundSpeed);
      
      Swal.fire({
        title: isCorrect ? 'ถูกต้อง! 🎉' : 'ผิด! 😔',
        text: isCorrect ? `คุณได้ ${scoreEarned} คะแนน!` : `คำตอบที่ถูกต้องคือ ${correctResult} (ได้ 0 คะแนน)`,
        icon: isCorrect ? 'success' : 'error',
        showCloseButton: false,
        showConfirmButton: true,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then(() => {
        setShowAnswerInput(false);
        if (onUserUpdate) {
          onUserUpdate(userData.username);
        }
      });
    }
  }, [currentResult, countdownTimer, timeoutTimer, userAnswer, checkAnswer, updateUserScore]);

  const displayReels = slotStopped ? finalReels : reels;

  return (
    <div className="slot-machine-2">
      <header className="game-header">
        <h1 className="game-title">Math Slot Machine</h1>
      </header>
      <div className="reels-container">
        {displayReels.map((reelSymbols, i) => (
          <div key={`reel-${i}-${slotStopped}`} className="reel-wrapper">
             <div 
               className={`reel-inner ${isSpinning ? 'spinning' : 'stopped'}`}
                 style={{ 
                  animationDelay: `${i * 0.3}s`
               }} 
             >
                {/* 
                   5 Reels:
                   0: Number
                   1: Operator
                   2: Number
                   3: Operator
                   4: Number
                */}
                <Reel symbols={reelSymbols} symbolType={(i === 1 || i === 3) ? 'operator' : 'number'} />
             </div>
          </div>
        ))}
      </div>
      <div className="controls">
        {showAnswerInput ? (
          <div className="answer-input-container">
            <div className="countdown">
              เวลาที่เหลือ: {countdown} วินาที <br/>
              <span style={{ fontSize: '0.8em', color: '#ffd700' }}>(คะแนนที่จะได้: {Math.round((countdown / 15) * 20)} คะแนน)</span>
            </div>
            <input
              type="number"
              inputMode="numeric"
              autoFocus
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyDown={(e) => {
                // ป้องกันตัวอักษรที่ไม่ใช่ตัวเลข (e, +, -, .)
                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                  e.preventDefault();
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit().catch(console.error);
                }
              }}
              placeholder="ใส่คำตอบ..."
              className="answer-input"
              disabled={answerDisabled}
            />
            <button onClick={() => handleSubmit().catch(console.error)} className="submit-button" disabled={answerDisabled}>
              ตอบคำถาม
            </button>
          </div>
        ) : (
          <button onClick={spin} disabled={isSpinning} className="spin-button">
            {isSpinning ? 'กำลังหมุน...' : 'หมุน'}
          </button>
        )}

      </div>
      <audio ref={slotOpenAudioRef} src={SlotgameOpen} preload="auto" loop />
      <audio ref={slotTurnAudioRef} src={SlotTurn} preload="auto" />
      <audio ref={countdownAudioRef} src={CountdownSound} preload="auto" />
    </div>
  );
}


